import { FundriseRouteConfig, NavigationGuard, Route } from 'vue-router';
import { app } from '@store/modules/app';
import { investmentEntity } from '@store/modules/investment-entity';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import { ServiceLevelName } from 'types/service-level';
import { trackMixpanel } from '@utils/analytics';

const isRepeatInvestor = (): boolean => {
	return app.isAuthenticated && investmentEntity.additionalInvestmentEligibility === 'VALID';
};

const questionnaireAuthGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (app.isAuthenticated) {
		await questionnaire.getFullResponse();

		if (investmentEntity.isClaimedGiftedAccount) {
			next({ name: 'account-overview', replace: true });
		} else if (questionnaire.isGiftedAccount) {
			next({ name: 'checkout-start', replace: true });
		} else if (questionnaire.dataRetrieved && !!questionnaire.fullResponse.completionDate) {
			next({ name: 'questionnaire-results' });
		} else if (questionnaire.fullResponse) {
			next({
				path: `/questionnaire/${questionnaire.fullResponse.questions[0].urlSlug}`,
				query: { firstStep: 'true', showPromoToast: 'true' }
			});
		}
	} else {
		next();
	}
};

export const questionnaireQuickInvestGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (isRepeatInvestor()) {
		next({ name: 'add-funds-ria' });
	}

	if (!questionnaire.dataRetrieved) {
		await questionnaire.getFullResponse(true);
	}

	if (questionnaire.isGiftedAccount) {
		next({ name: 'checkout-start', replace: true });
	} else {
		if (!orderForm.dataRetrieved) {
			await orderForm.getUserCheckoutDetailsData();
		}

		const starterServicename: ServiceLevelName = 'STARTER';
		const premiumServicename: ServiceLevelName = 'PREMIUM';
		const isRecommendedStarterPortfolio = questionnaire.fullResponse.recommendedServiceLevel === starterServicename;
		const isRecommendedPremium = questionnaire.fullResponse.recommendedServiceLevel === premiumServicename;
		const isQuickInvest = questionnaire.isQuickInvest;

		if (isRecommendedPremium || from.fullPath.includes('quick-invest')) {
			next();
		} else if (from.fullPath.includes('user-dashboard')) {
			if (isRecommendedStarterPortfolio) {
				next();
			} else {
				if (isQuickInvest) {
					await questionnaire.updateQuickInvest(false);
					trackMixpanel('Web Quick Invest Reset');
				}

				next({ name: 'quick-invest' });
			}
		} else {
			if (isRecommendedStarterPortfolio) {
				await questionnaire.updateQuickInvest(true);
				trackMixpanel('Web Quick Invest Automatically Selected');
				next({ name: 'checkout-start' });
			} else {
				if (isQuickInvest) {
					await questionnaire.updateQuickInvest(false);
					trackMixpanel('Web Quick Invest Reset');
				}

				next({ name: 'quick-invest' });
			}
		}
	}
};

export const quickInvestPageGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (!app.isAuthenticated) {
		next({ name: 'questionnaire-personal-info' });
	} else {
		await giftedAccountGuard(to, from, next);
	}
};

export const giftedAccountGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (!questionnaire.dataRetrieved) {
		await questionnaire.getFullResponse(true);
	}
	if (questionnaire.isGiftedAccount) {
		next({ name: 'checkout-start', replace: true });
	} else {
		next();
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const questionnaireRoutes: FundriseRouteConfig[] = [
	{
		path: `/questionnaire/results`,
		alias: '/questionnaire/results-cards',
		name: 'questionnaire-results',
		meta: { minTokenScopeRequired: 'UNVERIFIED' },
		component: () =>
			import(/* webpackChunkName: "questionnaire-results" */ '@views/questionnaire/questionnaire-results.vue'),
		beforeEnter: questionnaireQuickInvestGuard,
		redirect: { name: 'results-select-service-level' },
		children: [
			{
				path: '',
				name: 'results-select-service-level',
				meta: {
					step: 1,
					minTokenScopeRequired: 'UNVERIFIED'
				},
				component: () =>
					import(
						/* webpackChunkName: "results-select-service-level" */ '@views/questionnaire/questionnaire-results-select-service-level.vue'
					)
			},
			{
				path: 'select-core-plan',
				name: 'results-select-core-plan',
				meta: {
					step: 2,
					minTokenScopeRequired: 'UNVERIFIED'
				},
				beforeEnter: async (to, from, next) => {
					if (orderForm.serviceLevel === null) {
						next({ name: 'results-select-service-level' });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "results-select-core-plan" */ '@views/questionnaire/questionnaire-results-select-core-plan.vue'
					)
			},
			{
				path: `premium`,
				name: 'results-premium',
				meta: {
					step: 3,
					minTokenScopeRequired: 'UNVERIFIED'
				},
				component: () =>
					import(
						/* webpackChunkName: "results-premium" */
						'@views/questionnaire/questionnaire-results-premium.vue'
					)
			}
		]
	},
	{
		path: `/questionnaire`,
		name: 'questionnaire',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import(/* webpackChunkName: "questionnaire" */ '@views/questionnaire/questionnaire.vue'),
		redirect: { name: 'questionnaire-personal-info' },
		beforeEnter: async (to, from, next) => {
			if (isRepeatInvestor()) {
				next({ name: 'add-funds-ria' });
			}

			if (to.query.new_lead) {
				next({ name: 'questionnaire-password', query: { firstStep: 'true' } });
			} else {
				next();
			}
		},
		children: [
			{
				path: `personal-info`,
				name: 'questionnaire-personal-info',
				meta: {
					step: -2,
					minTokenScopeRequired: 'UNAUTHENTICATED'
				},
				beforeEnter: questionnaireAuthGuard,
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-personal-info" */ '@views/questionnaire/questionnaire-personal-info.vue'
					)
			},
			{
				path: `password`,
				name: 'questionnaire-password',
				meta: {
					step: -1,
					minTokenScopeRequired: 'UNAUTHENTICATED'
				},
				beforeEnter: questionnaireAuthGuard,
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-password" */ '@views/questionnaire/questionnaire-password.vue'
					)
			},
			{
				path: `quick-invest`,
				name: 'quick-invest',
				beforeEnter: quickInvestPageGuard,
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-quick-invest" */ '@views/questionnaire/questionnaire-quick-invest.vue'
					)
			},
			{
				path: `survey`,
				name: 'questionnaire-hdyh-survey',
				beforeEnter: giftedAccountGuard,
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-hdyh-survey" */ '@views/questionnaire/questionnaire-hdyh-survey.vue'
					)
			},
			{
				path: `account-type`,
				name: 'questionnaire-account-type',
				beforeEnter: giftedAccountGuard,
				meta: { minTokenScopeRequired: 'UNVERIFIED' },
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-account-type" */ '@src/views/questionnaire/questionnaire-account-type.vue'
					)
			},
			{
				path: `:questionUrlSlug`,
				name: 'questionnaire-question',
				meta: {
					step: (route: Route): number => {
						if (questionnaire.dataRetrieved) {
							return (
								questionnaire.fullResponse.questions.find(
									(q) => q.urlSlug === route.params.questionUrlSlug
								)?.sortIndex ?? -1
							);
						}

						return -1;
					},
					minTokenScopeRequired: 'UNVERIFIED'
				},
				beforeEnter: async (to, from, next) => {
					if (!questionnaire.dataRetrieved) {
						next({ name: 'questionnaire-personal-info' });
					} else {
						if (questionnaire.isGiftedAccount) {
							next({ name: 'checkout-start', replace: true });
						} else {
							next();
						}
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-question" */ '@views/questionnaire/questionnaire-question.vue'
					)
			}
		]
	}
];

export default questionnaireRoutes;
